import React, { useState, useEffect } from "react";
import {
    Box,
    Badge,
    Dialog,
    DialogTitle,
    DialogContent,
    Link,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Typography
} from "@mui/material/";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import GroupIcon from "@mui/icons-material/Group";
import { strings } from "../../../../common/i18n";
import { http } from "../../../../common/lib";
import config from "../../../../config";
import actions from "../../../../Store/actions";
import styles from "./styles";
import MatReactTable from "../../../../common/components/MaterialReactTable";
import useColumnsRows from "./useColumnsRows";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AddAssignments from "./AddAssignments";
import { useReissuedAsset } from "../../../../common/hooks";

/**
 * Audit details Component
 * When used from the audit list, pass auditId / open / onClose / onSaveComplete as props.
 * When used inside the review page, no props are needed (internal state is used).
 */
export default ({
    auditId: propAuditId,
    open: propOpen,
    onClose: propOnClose,
    onSaveComplete
} = {}) => {
    const isListContext = propAuditId !== undefined;

    const dispatch = useDispatch();
    const { accounts } = useSelector((state) => state);

    const [manageAuditor, setManageAuditor] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);

    const onOpenMangeAuditors = () => setManageAuditor(true);

    const onCloseManageAuditors = () => {
        setManageAuditor(false);
        onBack();
    };

    const handleClose = () => {
        onBack();
        propOnClose && propOnClose();
    };

    const {
        columns,
        data,
        loading,
        rowActions,
        showAuditorList,
        toolBarComponents,
        onBack
    } = useColumnsRows();

    const auditorList = data.map(({ email }) => email);
    const filteredAuditors = data.filter(
        ({ email }, index) => !auditorList.includes(email, index + 1)
    );

    let pendingAuditors = 0;
    data.map((item) => {
        if (item.statusId < 3) pendingAuditors++;
    });

    const canReissueAssets = useReissuedAsset();

    // Review-page behaviour: auto-open when assets need reissuing
    useEffect(() => {
        if (!isListContext) setManageAuditor(canReissueAssets);
    }, [canReissueAssets]);

    // List context: load required data into Redux when dialog opens
    useEffect(() => {
        if (!isListContext || !propOpen || !propAuditId) return;
        let cancelled = false;
        const load = async () => {
            setDataLoading(true);
            try {
                const calls = [
                    dispatch(
                        actions.assignmentDetails.create.getRequest(propAuditId)
                    ),
                    http.authorized
                        .get(
                            `${config.api.paths.auditLocations}?audit=${propAuditId}`
                        )
                        .then(({ data }) =>
                            dispatch(actions.auditLocations.create.set(data))
                        ),
                    dispatch(
                        actions.auditsList.create.itemSet({ id: propAuditId })
                    )
                ];
                if (!accounts.data.length) {
                    calls.push(dispatch(actions.accounts.create.getRequest()));
                }
                await Promise.all(calls);
            } catch (error) {
                if (!cancelled) console.error("ManageAuditors fetch:", error);
            } finally {
                if (!cancelled) setDataLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [propOpen, propAuditId]);

    const StyledBadge = styled(Badge)(({ theme }) => ({
        "& .MuiBadge-badge": {
            right: -15,
            top: 13,
            padding: "0 4px"
        }
    }));

    const dialogOpen = isListContext ? propOpen : manageAuditor;
    const dialogOnClose = isListContext ? handleClose : onCloseManageAuditors;

    const dialogContent = (
        <Dialog
            fullWidth={true}
            maxWidth={"lg"}
            onClose={dialogOnClose}
            aria-labelledby="customized-dialog-title"
            open={dialogOpen}
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {!showAuditorList ? (
                    strings("manageAuditors")
                ) : (
                    <>
                        {canReissueAssets && !isListContext ? (
                            strings("reissueAssets")
                        ) : (
                            <>
                                <IconButton onClick={onBack}>
                                    <ArrowBackIosNewIcon color="primary" />
                                </IconButton>
                                {strings("addAuditors")}
                            </>
                        )}
                    </>
                )}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={dialogOnClose}
                sx={(theme) => ({
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500]
                })}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers sx={styles.auditorContainer}>
                {!showAuditorList ? (
                    <MatReactTable
                        persistKey="reviewAuditors"
                        loading={loading || dataLoading}
                        columns={columns}
                        data={data}
                        rowActions={rowActions}
                        toolBarComponents={toolBarComponents}
                        noRowsMessage={strings("messages.noAuditors")}
                    />
                ) : (
                    <AddAssignments
                        onClose={dialogOnClose}
                        onSaveComplete={onSaveComplete}
                    />
                )}
            </DialogContent>
        </Dialog>
    );

    // List context: render only the dialog, no review-page sidebar UI
    if (isListContext) return dialogContent;

    return (
        <>
            <Box sx={styles.mainHeaderContainer}>
                <StyledBadge badgeContent={pendingAuditors} color="secondary">
                    <GroupIcon color="primary" />
                    <Typography variant="body1" sx={{ ml: 1 }}>
                        {strings("auditors")}
                    </Typography>
                </StyledBadge>
                <Link
                    component="button"
                    variant="caption"
                    onClick={onOpenMangeAuditors}
                >
                    {strings("manage")}
                </Link>
                <Box />
            </Box>
            <List sx={styles.auditorList}>
                {filteredAuditors.map((item) => (
                    <ListItem key={item.id} sx={styles.listItem}>
                        <ListItemText
                            primary={
                                <Typography variant="caption">
                                    {item.displayName?.length > 0
                                        ? item.displayName
                                        : "-"}
                                </Typography>
                            }
                        />
                    </ListItem>
                ))}
            </List>
            {dialogContent}
        </>
    );
};
